import PostReactions from "components/reactions/PostReactions";
import RelativeTime from "components/ui/RelativeTime";
import LinkButton from "components/ui/LinkButton";
import Link from "next/link";
import { useState } from "react";
import { useIntl } from "react-intl";
import type { IAssetDiscussionCommentFieldsFragment } from "../../lib/graphql/fragments/__generated__/AssetDiscussionCommentFields.generated";
import type { IAssetDiscussionCommentReplyFieldsFragment } from "../../lib/graphql/fragments/__generated__/AssetDiscussionCommentReplyFields.generated";
import useSelfie from "../../lib/hooks/useSelfie";
import translateTypenameIntoAssetType from "../../lib/utils/translateTypenameIntoAssetType";
import { IReactionSubject } from "../../types/graphqlTypes";
import Tags from "../black/Tags";
import Avatar from "../core/Avatar";
import { Menu, MenuActionButtonItem, MenuDivider } from "../core/menu";
import ActionsDropdown from "./ActionsDropdown";
import AssetDiscussionCommentReplyContour from "./AssetDiscussionCommentReplyContour";
import CommentContent from "./CommentContent";
import InlineAssetDiscussionCommentError from "./InlineAssetDiscussionCommentError";
import IndividualCommentInputForm from "./form/IndividualCommentInputForm";
import ReplyCommentInputForm from "./form/ReplyCommentInputForm";
import translateServerErrorIntoFriendlyMessage from "./form/translateServerErrorIntoFriendlyMessage";
import useCommentReplyActions from "./hooks/useCommentReplyActions";
import useReplyContour from "./hooks/useReplyContour";
import ReplyToAssetDiscussionCommentMutation from "./mutations/ReplyToAssetDiscussionCommentMutation";

interface IAssetDiscussionCommentReplyProps {
  reply: IAssetDiscussionCommentReplyFieldsFragment;
  rootComment: IAssetDiscussionCommentFieldsFragment;
  allowToReply?: boolean;
}

export default function AssetDiscussionCommentReply({
  reply,
  rootComment,
  allowToReply,
}: IAssetDiscussionCommentReplyProps) {
  const selfie = useSelfie();
  const intl = useIntl();

  const [isEditingContent, setIsEditingContent] = useState<boolean>(false);
  const [isWritingReply, setIsWritingReply] = useState<boolean>(false);

  const { destroyComment, editComment, isDestroyingComment, destroyCommentError, editCommentError } =
    useCommentReplyActions(reply, rootComment);

  const viewerCanTakeActions = reply.viewerCanEdit || reply.viewerCanDestroy;
  const hasContour = useReplyContour(reply.shortId);

  return (
    <div className="relative box-border">
      {hasContour && <AssetDiscussionCommentReplyContour />}

      <div className="flex items-start space-x-2">
        <Avatar
          src={reply.author.pictureUrl}
          hashValue={reply.author._id}
          alt={reply.author.fullName}
          size={40}
          squircle={true}
          variant="rounded"
        />

        <div className="flex flex-col">
          <div className="flex items-center space-x-2">
            <strong className="text-sm dark:text-slate-200">{reply.author.fullName}</strong>
            <Tags viewer={reply.author} />
          </div>

          <div>
            <Link
              href="/comments/[shortId]"
              as={`/comments/${rootComment.shortId}#${reply.shortId}`}
              passHref={true}
              className="hover:underline"
            >
              <RelativeTime className="text-xs font-medium text-slate-500 md:text-sm" timeStamp={reply.postedAt} />
            </Link>

            {reply.lastEditedAt && (
              <span className="text-xs font-medium text-slate-500 md:text-sm">{" • editado"}</span>
            )}
          </div>
        </div>
      </div>

      <div className="relative mt-4 space-y-2">
        {isEditingContent ? (
          <IndividualCommentInputForm
            submitButtonText="Editar"
            onSubmit={async (bodyAsPlainText) => {
              await editComment(bodyAsPlainText);
              setIsEditingContent(false);
            }}
            onCancel={() => setIsEditingContent(false)}
            bodyAsPlainText={reply.bodyAsPlainText}
          />
        ) : (
          <div className="pr-6">
            <CommentContent bodyAsPlainText={reply.bodyAsPlainText} />
          </div>
        )}

        <PostReactions
          subjectId={reply._id}
          subjectType={IReactionSubject.AssetDiscussionCommentReply}
          reactable={reply}
        />

        {((rootComment.viewerCanReply && allowToReply) || viewerCanTakeActions) && (
          <div className="grid grid-cols-2 pt-2">
            <div className="grid auto-cols-max grid-flow-col gap-x-3">
              {rootComment.viewerCanReply && allowToReply && (
                <LinkButton variant="primary" onClick={() => setIsWritingReply(!isWritingReply)}>
                  Responder
                </LinkButton>
              )}

              {viewerCanTakeActions && (
                <ActionsDropdown>
                  {({ closeOnClick }) => (
                    <Menu>
                      {reply.viewerCanEdit && (
                        <MenuActionButtonItem
                          variant="primary"
                          onClick={closeOnClick(() => setIsEditingContent(!isEditingContent))}
                        >
                          Editar
                        </MenuActionButtonItem>
                      )}

                      {selfie && selfie.isSiteAdmin && (
                        <>
                          <MenuActionButtonItem
                            variant="primary"
                            onClick={closeOnClick(() => {
                              navigator.clipboard.writeText(reply.author.email);
                            })}
                          >
                            Copiar email
                          </MenuActionButtonItem>

                          <MenuActionButtonItem
                            variant="primary"
                            onClick={closeOnClick(() => {
                              navigator.clipboard.writeText(reply.author._id);
                            })}
                          >
                            Copiar ID
                          </MenuActionButtonItem>
                        </>
                      )}

                      <MenuDivider />

                      {reply.viewerCanDestroy && (
                        <MenuActionButtonItem
                          variant="danger"
                          disabled={isDestroyingComment}
                          onClick={() => {
                            // eslint-disable-next-line no-alert, no-restricted-globals
                            const didConfirm = confirm(
                              intl.formatMessage({
                                defaultMessage: "Tem certeza que deseja apagar a resposta do comentário?",
                                id: "o505UE",
                              }),
                            );

                            if (didConfirm) {
                              destroyComment();
                            }
                          }}
                        >
                          {isDestroyingComment ? "Apagando..." : "Apagar"}
                        </MenuActionButtonItem>
                      )}
                    </Menu>
                  )}
                </ActionsDropdown>
              )}
            </div>
          </div>
        )}

        {selfie && isWritingReply && (
          <div className="pt-2">
            <ReplyToAssetDiscussionCommentMutation>
              {(replyToAssetDiscussionComment) => (
                <div className="py-4">
                  <ReplyCommentInputForm
                    viewer={selfie}
                    autoFocus={true}
                    onCancel={() => setIsWritingReply(false)}
                    onSubmit={async (bodyAsPlainText) => {
                      await replyToAssetDiscussionComment({
                        assetId: rootComment.asset._id,
                        assetType: translateTypenameIntoAssetType(rootComment.asset.__typename),
                        replyToCommentId: rootComment._id,
                        replyToCommentShortId: rootComment.shortId,
                        bodyAsPlainText,
                      });
                      setIsWritingReply(false);
                    }}
                  />
                </div>
              )}
            </ReplyToAssetDiscussionCommentMutation>
          </div>
        )}

        {firstError([destroyCommentError, editCommentError]) && (
          <div>
            <InlineAssetDiscussionCommentError>
              {translateServerErrorIntoFriendlyMessage(
                firstError([destroyCommentError, editCommentError]) ||
                  new Error("Whoopz...deu algo errado aqui. Tente novamente mais tarde."),
                intl,
              )}
            </InlineAssetDiscussionCommentError>
          </div>
        )}
      </div>
    </div>
  );
}

function firstError(arrayOfErrors: (Error | null)[]): Error | null {
  return arrayOfErrors.find((error) => error !== null) || null;
}
